import React, { useEffect, useRef } from 'react';
import { useParams, useSearchParams, Outlet } from 'react-router-dom';
import { useRestaurantContext } from '../../context/RestaurantContext';

/**
 * RestaurantShell
 *
 * Mounted at /restaurant/:restaurantId (and its sub-routes).
 * Reads restaurantId from the URL every render — so a page refresh always
 * re-triggers the data load instead of getting stuck at isLoading.
 *
 * Table number comes from ?table=X and is stored in context for checkout.
 */
const RestaurantShell = () => {
  const { restaurantId } = useParams();
  const [searchParams] = useSearchParams();

  const {
    loadRestaurant,
    isLoading,
    error,
    setTableNumber,
    setIsTrackedTable,
    setTableSection,
    setInitialOrderType,
    setUrlModeOverride,
  } = useRestaurantContext();

  const tableIdParam = searchParams.get('tableId');
  const tableParam = searchParams.get('table');
  const trackedParam = searchParams.get('tracked');
  const sectionParam = searchParams.get('section') || searchParams.get('location');
  const typeParam = searchParams.get('type') || searchParams.get('orderType');
  const modeParam = searchParams.get('mode');

  // Track last loaded ID and tableId so hot-reloads don't re-fetch unnecessarily
  const lastLoadedRef = useRef({ restaurantId: null, tableId: null });

  useEffect(() => {
    if (tableIdParam && restaurantId) {
      sessionStorage.setItem(`swado_table_id_${restaurantId}`, tableIdParam);
    }

    if (tableParam) {
      setTableNumber(tableParam);
      if (restaurantId) {
        sessionStorage.setItem(`swado_table_${restaurantId}`, tableParam);
        const isTracked = trackedParam !== 'false';
        setIsTrackedTable(isTracked);
        sessionStorage.setItem(`swado_tracked_${restaurantId}`, String(isTracked));
      }
    } else if (restaurantId) {
      const savedTable = sessionStorage.getItem(`swado_table_${restaurantId}`);
      if (savedTable) {
        setTableNumber(savedTable);
        const savedTracked = sessionStorage.getItem(`swado_tracked_${restaurantId}`);
        setIsTrackedTable(savedTracked !== 'false');
      }
    }

    if (sectionParam) {
      setTableSection(sectionParam);
      if (restaurantId) sessionStorage.setItem(`swado_section_${restaurantId}`, sectionParam);
    } else if (restaurantId) {
      const savedSection = sessionStorage.getItem(`swado_section_${restaurantId}`);
      if (savedSection) setTableSection(savedSection);
    }

    if (typeParam) {
      // Map alias 'pickup' to standard 'takeaway'
      const normalizedType = typeParam === 'pickup' ? 'takeaway' : typeParam;
      setInitialOrderType(normalizedType);
      if (restaurantId) sessionStorage.setItem(`swado_type_${restaurantId}`, normalizedType);
    } else if (restaurantId) {
      const savedType = sessionStorage.getItem(`swado_type_${restaurantId}`);
      if (savedType) setInitialOrderType(savedType);
    }

    if (modeParam) {
      setUrlModeOverride(modeParam);
    }
  }, [tableIdParam, tableParam, trackedParam, sectionParam, typeParam, modeParam, restaurantId, setTableNumber, setIsTrackedTable, setTableSection, setInitialOrderType, setUrlModeOverride]);

  useEffect(() => {
    if (restaurantId && (restaurantId !== lastLoadedRef.current.restaurantId || tableIdParam !== lastLoadedRef.current.tableId)) {
      lastLoadedRef.current = { restaurantId, tableId: tableIdParam };
      loadRestaurant(restaurantId, tableIdParam);
    }
  }, [restaurantId, tableIdParam, loadRestaurant]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Skeleton header */}
        <div className="relative w-full bg-white shadow-sm">
          <div className="w-full h-32 bg-gray-200 animate-pulse" />
          <div className="absolute top-20 left-4 h-16 w-16 bg-white rounded-full p-1 shadow-md">
            <div className="w-full h-full rounded-full bg-gray-300 animate-pulse" />
          </div>
          <div className="pt-10 p-4 space-y-2">
            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
            <div className="h-3 w-28 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-400 text-sm animate-pulse">Loading menu…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-gray-500 gap-3 p-6">
        <p className="text-4xl">😕</p>
        <p className="text-base font-semibold">Could not load restaurant</p>
        <p className="text-sm text-center">{error}</p>
        <button
          onClick={() => loadRestaurant(restaurantId)}
          className="mt-2 px-4 py-2 bg-gray-800 text-white rounded-full text-sm"
        >
          Try again
        </button>
      </div>
    );
  }

  // Render the matched child route (MenuPage, CheckoutFlow, OrderHistory)
  return <Outlet />;
};

export default RestaurantShell;
